import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, ShieldCheck, type LucideIcon } from 'lucide-react';
import { SifPreflightView, WpsDeadlineView } from '../../../../../models/payroll';

interface KsaSummaryCardsProps {
  isKsa: boolean;
  preflight: SifPreflightView | null;
  deadline: WpsDeadlineView | null;
  noPayrollForPeriod: boolean;
  companyChecksFailed: boolean;
  companyFailCount: number;
}

export const KsaSummaryCards: React.FC<KsaSummaryCardsProps> = ({
  isKsa,
  preflight,
  deadline,
  noPayrollForPeriod,
  companyChecksFailed,
  companyFailCount,
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-3" data-testid="wps-summary">
      {/* No payroll for the month yet (0 employees): nothing has failed, the month is simply not generated. */}
      <SummaryCard
        icon={ShieldCheck}
        tone={noPayrollForPeriod ? 'neutral' : preflight?.canGenerate ? 'ok' : 'error'}
        label={isKsa ? t('payroll.wps.preflightKsa', 'Mudad Pre-flight') : t('SIF Pre-flight')}
        value={
          !preflight
            ? '—'
            : noPayrollForPeriod
              ? t('payroll.wps.generateFirst', "Generate this month's payroll first")
              : preflight.canGenerate
                ? t('Ready')
                : t('{{n}} issues', { n: preflight.employeeIssues?.length ?? 0 })
        }
        testId="card-preflight"
      />
      <SummaryCard
        icon={ShieldCheck}
        tone={preflight ? (companyChecksFailed ? 'error' : 'ok') : 'ok'}
        label={t('payroll.wps.companySetup', 'Company Setup')}
        value={preflight ? (companyChecksFailed ? t('{{n}} issues', { n: companyFailCount }) : t('All passed')) : '—'}
        testId="card-company-setup"
      />
      <SummaryCard
        icon={AlertTriangle}
        tone={deadline?.zone === 'Green' ? 'ok' : deadline?.zone === 'Amber' ? 'warn' : 'error'}
        label={t('Deadline')}
        value={deadline ? `${deadline.daysRemaining}d ${deadline.zone}` : '—'}
        testId="card-deadline"
      />
      <SummaryCard
        icon={CheckCircle2}
        tone={preflight && preflight.employeesTotal > 0 && preflight.employeesPassed < preflight.employeesTotal ? 'error' : 'ok'}
        label={t('Employees Passing')}
        value={preflight ? `${preflight.employeesPassed}/${preflight.employeesTotal}` : '—'}
        testId="card-passing"
      />
    </div>
  );
};

interface SummaryCardProps {
  icon: LucideIcon;
  tone: 'ok' | 'warn' | 'error' | 'neutral';
  label: string;
  value: string;
  testId?: string;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ icon: Icon, tone, label, value, testId }) => {
  const tones = {
    ok: 'border-emerald-500/30 bg-emerald-500/5 text-emerald-700',
    warn: 'border-amber-500/30 bg-amber-500/5 text-amber-700',
    error: 'border-destructive/30 bg-destructive/5 text-destructive',
    neutral: 'border-border bg-muted/40 text-muted-foreground',
  } as const;
  return (
    <div className={`rounded-lg border px-3 py-2 flex items-center gap-2 ${tones[tone]}`} data-testid={testId}>
      <Icon size={18} />
      <div className="leading-tight">
        <p className="text-[11px] uppercase tracking-wide opacity-70">{label}</p>
        <p className="text-sm font-semibold">{value}</p>
      </div>
    </div>
  );
};
